import React, { useState, useEffect } from 'react';
import { Form, Button, FormGroup, FormControl, FormLabel } from 'react-bootstrap';
import TiptapEditor from '../../../components/TiptapEditor/TiptapEditor';
import '../../../theme/tiptap-editor.scss';
import '../../../theme/modal-consistency.scss';
import Swal from 'sweetalert2';

const LecturaForm = ({ lectura, setLectura, handleSubmit, buttonText }) => {
    // Local states for evaluations and freeResponse
    const [localEvaluations, setLocalEvaluations] = useState([]);
    const [localFreeResponse, setLocalFreeResponse] = useState({ title: '', description: '' });

    // Initialize local states only when `lectura` changes
    useEffect(() => {
        if (lectura && lectura.evaluations) {
            setLocalEvaluations(lectura.evaluations);
        } else {
            setLocalEvaluations([]);
        }
        if (lectura && lectura.freeResponseEvaluation) {
            setLocalFreeResponse(lectura.freeResponseEvaluation);
        } else {
            setLocalFreeResponse({ title: '', description: '' });
        }
    }, [lectura]);

    // Synchronize local states with the parent component
    useEffect(() => {
        if (!lectura) return; // Guard clause to prevent errors when lectura is null
        
        setLectura((prev) => {
            // Prevent unnecessary updates
            if (
                prev && 
                JSON.stringify(prev.evaluations) === JSON.stringify(localEvaluations) &&
                JSON.stringify(prev.freeResponseEvaluation) === JSON.stringify(localFreeResponse)
            ) {
                return prev;
            }
            return {
                ...prev,
                evaluations: localEvaluations,
                freeResponseEvaluation: localFreeResponse,
            };
        });
    }, [localEvaluations, localFreeResponse, setLectura, lectura]);

    const handleEvaluationChange = (index, field, value) => {
        const updatedEvaluations = [...localEvaluations];
        if (field === 'title') {
            updatedEvaluations[index].title = value;
        } else if (field.startsWith('answer')) {
            const answerIndex = parseInt(field.split('-')[1], 10);
            updatedEvaluations[index].answers[answerIndex] = value;
        }
        setLocalEvaluations(updatedEvaluations);
    };

    const addEvaluation = () => {
        const newEvaluation = { type: 'selection', title: '', answers: ['', '', '', '', ''], showAnswer5: false };
        setLocalEvaluations([...localEvaluations, newEvaluation]);
    };

    const removeEvaluation = (index) => {
        if (localEvaluations.length === 1) {
            Swal.fire({
                icon: 'warning',
                title: 'Atención',
                text: 'Debe haber al menos una evaluación.',
            });
            return;
        }
        const updatedEvaluations = localEvaluations.filter((_, i) => i !== index);
        setLocalEvaluations(updatedEvaluations);
    };

    const toggleAnswer5 = (index) => {
        const updatedEvaluations = [...localEvaluations];
        if (updatedEvaluations[index].showAnswer5) {
            updatedEvaluations[index].answers[4] = ''; // Clear the fifth answer if toggled off
        }
        updatedEvaluations[index].showAnswer5 = !updatedEvaluations[index].showAnswer5;
        setLocalEvaluations(updatedEvaluations);
    };

    const handleFreeResponseChange = (field, value) => {
        setLocalFreeResponse((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        
        // Validate that lectura exists before proceeding
        if (!lectura) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se puede guardar la lectura. Datos no válidos.',
            });
            return;
        }
        
        const nuevaLectura = {
            ...lectura,
            evaluations: localEvaluations.map((evaluation) => ({
                title: evaluation.title,
                answers: evaluation.answers.filter((answer) => answer !== ''), // Filter empty answers
            })),
            freeResponseEvaluation: localFreeResponse,
        };
        
        handleSubmit(nuevaLectura);
        Swal.fire({
            icon: 'success',
            title: 'Lectura guardada con éxito',
            showConfirmButton: false,
            timer: 2000,
        });
    };

    return (
        <Form className="lesson-form-shell" onSubmit={handleFormSubmit}>
            <div className="lesson-form-grid">
                <FormGroup className="mb-3">
                    <FormLabel>Nivel</FormLabel>
                    <Form.Select
                        value={lectura?.level || ''}
                        onChange={(e) => setLectura({ ...lectura, level: e.target.value })}
                    >
                        <option value="">Selecciona un nivel</option>
                        <option value="1">Nivel 1</option>
                        <option value="2">Nivel 2</option>
                        <option value="3">Nivel 3</option>
                    </Form.Select>
                </FormGroup>
                <FormGroup className="mb-3 lesson-title-field">
                    <FormLabel>Nombre de la Lectura</FormLabel>
                    <FormControl
                        type="text"
                        value={lectura?.nombre || ''}
                        onChange={(e) => setLectura({ ...lectura, nombre: e.target.value })}
                        placeholder="Ingresa el nombre de la lectura"
                    />
                </FormGroup>
                <FormGroup className="mb-3">
                    <FormLabel>Columnas</FormLabel>
                    <Form.Select
                        value={lectura?.columnas || '1'}
                        onChange={(e) => setLectura({ ...lectura, columnas: e.target.value })}
                    >
                        <option value="1">1 columna</option>
                        <option value="2">2 columnas</option>
                    </Form.Select>
                </FormGroup>
            </div>

            <FormGroup className="mb-3">
                <FormLabel>Descripción</FormLabel>
                <TiptapEditor
                    value={lectura?.descripcion || ''}
                    onChange={content => setLectura({ ...lectura, descripcion: content })}
                    placeholder="Ingresa una descripción de la lectura"
                    imageFolder="lecturas-adicionales"
                    columnCount={lectura?.columnas || '1'}
                    className="lesson-tiptap-editor lectura-descripcion-editor"
                />
            </FormGroup>

            <h4>Evaluaciones</h4>
            {localEvaluations.map((evaluation, index) => (
                <div key={index} className="mb-4 p-3 border rounded">
                    <FormGroup className="mb-3">
                        <FormLabel>Título de la Evaluación</FormLabel>
                        <FormControl
                            type="text"
                            value={evaluation.title}
                            onChange={(e) => handleEvaluationChange(index, 'title', e.target.value)}
                            placeholder="Ingresa el título de la evaluación"
                        />
                    </FormGroup>
                    <FormLabel>Respuestas</FormLabel>
                    {evaluation.answers.slice(0, evaluation.showAnswer5 ? 5 : 4).map((answer, answerIndex) => (
                        <FormGroup key={answerIndex} className="mb-2">
                            <FormControl
                                type="text"
                                value={answer}
                                onChange={(e) => handleEvaluationChange(index, `answer-${answerIndex}`, e.target.value)}
                                placeholder={`Respuesta ${answerIndex + 1}`}
                            />
                        </FormGroup>
                    ))}
                    <Button variant="link" onClick={() => toggleAnswer5(index)}>
                        {evaluation.showAnswer5 ? 'Quitar Respuesta 5' : 'Agregar Respuesta 5'}
                    </Button>
                    <Button variant="danger" onClick={() => removeEvaluation(index)} className="mt-2">
                        Eliminar Evaluación
                    </Button>
                </div>
            ))}
            <Button variant="success" onClick={addEvaluation} className="mb-4">
                Añadir Evaluación de Selección Simple
            </Button>

            <h4>Evaluación de Respuesta Libre</h4>
            <FormGroup className="mb-3">
                <FormLabel>Título de la Evaluación</FormLabel>
                <FormControl
                    type="text"
                    value={localFreeResponse.title}
                    onChange={(e) => handleFreeResponseChange('title', e.target.value)}
                    placeholder="Ingresa el título de la evaluación"
                />
            </FormGroup>
            <FormGroup className="mb-3">
                <FormLabel>Descripción (opcional)</FormLabel>
                <TiptapEditor
                    value={localFreeResponse.description || ''}
                    onChange={content => handleFreeResponseChange('description', content)}
                    placeholder="Ingresa una descripción para la evaluación"
                    imageFolder="lecturas-adicionales"
                    columnCount={lectura?.columnas || '1'}
                    className="lesson-tiptap-editor lectura-free-response-editor"
                />
            </FormGroup>

            <Button variant="primary" type="submit">
                {buttonText}
            </Button>
        </Form>
    );
};

export default LecturaForm;
